import { IPointsData } from "@interfaces";

/**
 * TEMPORARY local preview of the points / commission Order Summary.
 *
 * Those sections are gated on agent flags the backend derives from TDMS
 * (`is_points_agent`, `is_commission_agent`), so they can't be seen on an
 * account that isn't a points or commission agent — which makes the UI hard to
 * work on. This substitutes plausible values so the sections render.
 *
 * Two deliberate safeguards, because this fakes entitlement state:
 *  - it is inert unless the URL carries `?pointsPreview=points|commission`;
 *  - it is compiled out of any production build (`NODE_ENV === "production"`).
 * It only changes what this screen *displays*; nothing is sent to the backend,
 * and a real booking would still be priced by the server from the real account.
 *
 * Delete this file and its two call sites in CheckoutStep3 once the agent
 * upgrade works on a test account.
 */

export type PointsPreviewMode = "points" | "commission";

/** Obvious fake figures — round numbers nobody would mistake for real balances. */
const PREVIEW_POINTS_DATA: IPointsData = {
    subSystemType: "PREVIEW",
    pointsBalance: 5000,
    availableBalance: 5000,
    pointsMultiplier: 100,
};

/** Points earned on this order, standing in for the verified server value. */
const PREVIEW_POINTS_AVAILABLE = 2500;

/** The mode named in the query string, or null when the preview is off. */
export const getPointsPreviewMode = (search: string): PointsPreviewMode | null => {
    if (process.env.NODE_ENV === "production") return null;
    const value = new URLSearchParams(search).get("pointsPreview")?.trim().toLowerCase();
    if (value === "points" || value === "commission") return value;
    return null;
};

interface IPointsFlags {
    isAgent?: boolean;
    isPointsAgent: boolean;
    isCommissionAgent: boolean;
    isPointsDisplayed?: boolean;
    pointsData: IPointsData;
    pointsAvailable?: number;
}

/**
 * Returns `flags` unchanged when the preview is off, and a spoofed set when it
 * is on. The two modes are mutually exclusive upstream, so they are here too.
 */
export const applyPointsPreview = (
    flags: IPointsFlags,
    mode: PointsPreviewMode | null,
): IPointsFlags => {
    if (!mode) return flags;
    return {
        isAgent: true,
        isPointsAgent: mode === "points",
        isCommissionAgent: mode === "commission",
        isPointsDisplayed: true,
        pointsData: PREVIEW_POINTS_DATA,
        pointsAvailable: PREVIEW_POINTS_AVAILABLE,
    };
};
